import { useState } from 'react';
import { articles, faqs } from '@/mocks/homeData';

export default function ArticlesFaqSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-100" id="faq">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center rounded bg-accent-200/70">
              <i className="ri-file-text-line text-xs text-accent-600"></i>
            </div>
            <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">Resources</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
            Latest <span className="text-accent-500">Guides &amp; Reviews</span>
          </h2>
        </div>

        {/* Grid: Articles + FAQ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Article Card 1 */}
          <a
            href="/vigorbuy-sheet-guide"
            className="bg-background-50 rounded-lg p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/70 transition-colors duration-200"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-secondary-100/70 flex items-center justify-center">
                <i className="ri-guide-line text-lg text-secondary-600"></i>
              </div>
              <span className="text-[10px] font-semibold text-secondary-700 uppercase tracking-wider mt-1 whitespace-nowrap">
                {articles[0].label}
              </span>
            </div>
            <i className="ri-double-quotes-l text-xl text-accent-300 mb-3"></i>
            <p className="text-sm font-heading font-bold text-foreground-900 mb-2 leading-snug">
              {articles[0].title}
            </p>
            <p className="text-xs text-foreground-600 leading-relaxed mt-auto">
              {articles[0].excerpt}
            </p>
          </a>

          {/* Article Card 2 */}
          <a
            href="/vigorbuy-sheet-guide"
            className="bg-background-50 rounded-lg p-5 md:p-6 flex flex-col cursor-pointer hover:bg-background-50/70 transition-colors duration-200"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-primary-100/70 flex items-center justify-center">
                <i className="ri-star-line text-lg text-primary-500"></i>
              </div>
              <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-wider mt-1 whitespace-nowrap">
                {articles[1].label}
              </span>
            </div>
            <i className="ri-double-quotes-l text-xl text-primary-200 mb-3"></i>
            <p className="text-sm font-heading font-bold text-foreground-900 mb-2 leading-snug">
              {articles[1].title}
            </p>
            <p className="text-xs text-foreground-600 leading-relaxed mt-auto">
              {articles[1].excerpt}
            </p>
          </a>

          {/* Reviews Image Card */}
          <div className="bg-background-50 rounded-lg overflow-hidden relative h-[280px] lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=A%20modern%20workspace%20with%20a%20laptop%20showing%20five%20star%20ratings%20on%20screen%2C%20beside%20it%20a%20minimalist%20notebook%2C%20warm%20desk%20lamp%20glow%2C%20cozy%20evening%20atmosphere%2C%20editorial%20photography%2C%20trust%20and%20reliability%20theme%2C%20no%20text%20or%20logos&width=600&height=800&seq=reviews-new-card&orientation=portrait"
              alt="VigorBuy user reviews and trusted buyers"
              title="VigorBuy Spreadsheet - Trusted by Buyers"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5">
              <p className="text-white text-sm font-semibold">User Reviews</p>
              <p className="text-white/70 text-xs">Trusted by Buyers Worldwide</p>
            </div>
          </div>

          {/* FAQ Section - bottom row on desktop */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mt-0">
            {faqs.slice(0, 4).map((faq, idx) => (
              <div
                key={faq.question}
                className="bg-background-50 rounded-lg p-4 md:p-5 cursor-pointer hover:bg-background-50/70 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              >
                <div className="flex items-start gap-3 mb-2">
                  <i className="ri-question-line text-lg text-accent-400 flex-shrink-0 mt-0.5"></i>
                  <div className="flex-1">
                    <p className="text-sm font-heading font-bold text-foreground-900 leading-snug">{faq.question}</p>
                  </div>
                </div>
                {openFaq === idx && (
                  <p className="text-xs text-foreground-600 leading-relaxed mt-3 pl-8 pr-2">
                    {faq.answer}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3 pl-8">
                  <span className="text-[10px] font-semibold text-accent-600 bg-accent-100/60 px-2 py-0.5 rounded-full whitespace-nowrap">{faq.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Wide Article Card - bottom row */}
          <div className="lg:col-span-1">
            <a
              href="/vigorbuy-sheet-guide"
              className="block bg-background-50 rounded-lg overflow-hidden relative h-full min-h-[200px] cursor-pointer group"
            >
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimalist%20desk%20with%20an%20open%20laptop%20beside%20a%20stack%20of%20fashion%20catalogs%2C%20a%20small%20indoor%20plant%2C%20morning%20daylight%20through%20a%20window%2C%20warm%20editorial%20photography%2C%20organized%20workspace%20aesthetic%2C%20no%20text%20or%20logos&width=600&height=500&seq=guide-new-card-wide&orientation=landscape"
                alt="Complete VigorBuy Sheet Guide 2026"
                title="Complete VigorBuy Sheet Guide 2026"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white text-sm font-heading font-bold mb-1 leading-snug">Complete VigorBuy Sheet Guide 2026</p>
                <p className="text-white/70 text-xs leading-relaxed line-clamp-2">
                  Everything you need to know about using the VigorBuy spreadsheet effectively — from finding products to safe checkout.
                </p>
                <span className="inline-flex items-center gap-1 text-white/80 text-xs mt-3 font-semibold whitespace-nowrap">
                  Read Guide <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}